import React from "react";
import { Link } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

const stats = [
  { value: "5,000+", label: "Gyms Listed", icon: "bi-buildings" },
  { value: "50+",    label: "Cities Covered", icon: "bi-geo-alt-fill" },
  { value: "1L+",    label: "Happy Members", icon: "bi-people-fill" },
  { value: "4.8★",   label: "Average Rating", icon: "bi-star-fill" },
];

const values = [
  {
    num: "01",
    icon: "bi-eye-fill",
    title: "Transparency",
    tagline: "What you see is what you get.",
    desc: "Real reviews, real prices, real facilities — no paid placements, no boosted listings, no hidden agendas. Every piece of information on MeraGym is honest and unfiltered.",
    color: "#3b82f6",
    bg: "#eff6ff",
  },
  {
    num: "02",
    icon: "bi-shield-fill-check",
    title: "Trust",
    tagline: "Verified before it goes live.",
    desc: "Every gym on MeraGym is manually verified by our team before being published. We stand behind every listing so you can join any gym with full confidence.",
    color: "#10b981",
    bg: "#f0fdf4",
  },
  {
    num: "03",
    icon: "bi-heart-fill",
    title: "Accessibility",
    tagline: "Fitness is for everyone.",
    desc: "MeraGym is 100% free for gym seekers — no paywalls, no sign-up fees, no subscriptions. We believe cost should never be a barrier to finding the right place to get fit.",
    color: "#f59e0b",
    bg: "#fffbeb",
  },
  {
    num: "04",
    icon: "bi-lightning-charge-fill",
    title: "Speed",
    tagline: "From search to join in minutes.",
    desc: "Our smart search, filters, and verified listings are engineered to cut your gym-hunting time from weeks to minutes — so you spend more time working out and less time searching.",
    color: "#8b5cf6",
    bg: "#f5f3ff",
  },
];

const team = [
  {
    name: "MeraGym Team",
    role: "Founders & Builders",
    location: "Varanasi & Noida",
    icon: "bi-people-fill",
  },
];

export default function AboutUsPage() {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>About Us | MeraGym — India's Gym Discovery Platform</title>
          <meta
            name="description"
            content="Learn about MeraGym — India's leading gym discovery platform. Our mission is to make fitness accessible to everyone by connecting people with the best gyms, trainers, and fitness centers near them."
          />
          <meta
            name="keywords"
            content="about meragym, meragym story, gym discovery India, meragym mission, fitness platform India, meragym team, gym listing platform"
          />
          <link rel="canonical" href="https://meragym.com/about-us" />
          <meta property="og:title" content="About MeraGym — India's Gym Discovery Platform" />
          <meta property="og:description" content="MeraGym connects fitness seekers with 5,000+ verified gyms across 50+ cities in India. Learn our story, mission, and values." />
          <meta property="og:url" content="https://meragym.com/about-us" />
          <meta property="og:type" content="website" />
        </Helmet>
      </HelmetProvider>

      {/* ── Hero ── */}
      <section className="about-hero">
        <div className="about-hero-glow about-hero-glow-1" aria-hidden="true"></div>
        <div className="about-hero-glow about-hero-glow-2" aria-hidden="true"></div>
        <div className="container">
          <div className="about-hero-inner">
            <span className="about-hero-pill"><i className="bi bi-buildings me-1"></i> India's Gym Discovery Platform</span>
            <h1 className="about-hero-title">
              We're on a Mission to Make<br />
              <span className="about-hero-accent">Fitness Accessible for Everyone</span>
            </h1>
            <p className="about-hero-desc">
              MeraGym was built with one goal — to remove every barrier between a person and the gym they deserve.
              No confusion. No wasted time. Just find, compare, and join.
            </p>
            <div className="about-hero-actions">
              <Link to="/varanasi/gym" className="about-btn-primary">
                <i className="bi bi-search me-2"></i>Find Gyms Near Me
              </Link>
              <Link to="/contact-us" className="about-btn-secondary">
                <i className="bi bi-chat-dots me-2"></i>Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <div className="about-stats-bar">
        <div className="container">
          <div className="about-stats-grid">
            {stats.map((s) => (
              <div className="about-stat-item" key={s.label}>
                <i className={`bi ${s.icon} about-stat-icon`}></i>
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Our Story ── */}
      <section className="about-section">
        <div className="container">
          <div className="about-story-grid">
            <div className="about-story-text">
              <span className="about-section-pill">Our Story</span>
              <h2 className="about-section-title">Born in Varanasi. Built for India.</h2>
              <p>
                MeraGym started with a frustration that millions of Indians share — finding a good gym nearby shouldn't be hard.
                Yellow pages were outdated. Google results were unreliable. Word of mouth was slow.
              </p>
              <p>
                So in 2023, our team at <strong>Techup Technologies</strong> set out to build the most comprehensive,
                honest, and easy-to-use gym discovery platform in India. Starting from Varanasi and Noida,
                we went city by city, verifying every gym, collecting real reviews, and building the tools
                that fitness seekers actually needed.
              </p>
              <p>
                Today, MeraGym lists <strong>5,000+ verified fitness centers</strong> across <strong>50+ cities</strong>,
                and we're just getting started. Our free platform is trusted by lakhs of fitness seekers every month
                — from first-timers looking for a beginner gym to athletes hunting for a specialised CrossFit box.
              </p>
            </div>
            <div className="about-story-visual">
              <div className="about-story-card">
                <div className="about-story-card-icon"><i className="bi bi-geo-alt-fill"></i></div>
                <div className="about-story-card-body">
                  <strong>Headquartered in</strong>
                  <span>Kamachha, Varanasi &amp; Sector 75, Noida</span>
                </div>
              </div>
              <div className="about-story-card">
                <div className="about-story-card-icon"><i className="bi bi-calendar2-check"></i></div>
                <div className="about-story-card-body">
                  <strong>Founded in</strong>
                  <span>2023 by Techup Technologies</span>
                </div>
              </div>
              <div className="about-story-card">
                <div className="about-story-card-icon"><i className="bi bi-trophy-fill"></i></div>
                <div className="about-story-card-body">
                  <strong>Recognition</strong>
                  <span>Trusted by 5,000+ gyms across India</span>
                </div>
              </div>
              <div className="about-story-card">
                <div className="about-story-card-icon"><i className="bi bi-phone-fill"></i></div>
                <div className="about-story-card-body">
                  <strong>Available on</strong>
                  <span>Web &amp; Android (Google Play)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Mission & Vision ── */}
      <section className="about-mv-section">
        <div className="container">
          <div className="about-mv-grid">
            <div className="about-mv-card about-mv-mission">
              <div className="about-mv-icon"><i className="bi bi-bullseye"></i></div>
              <h3>Our Mission</h3>
              <p>
                To make fitness accessible to every Indian by connecting them with the right gym,
                trainer, or fitness center — quickly, transparently, and for free.
              </p>
              <ul className="about-mv-list">
                <li><i className="bi bi-check-circle-fill"></i> Remove barriers to fitness discovery</li>
                <li><i className="bi bi-check-circle-fill"></i> Empower gym owners to grow online</li>
                <li><i className="bi bi-check-circle-fill"></i> Deliver honest, verified information</li>
              </ul>
            </div>
            <div className="about-mv-card about-mv-vision">
              <div className="about-mv-icon"><i className="bi bi-eye-fill"></i></div>
              <h3>Our Vision</h3>
              <p>
                To become India's most trusted fitness ecosystem — where finding, joining, and managing
                your fitness journey is as seamless as ordering food online.
              </p>
              <ul className="about-mv-list">
                <li><i className="bi bi-check-circle-fill"></i> Gyms in every city, town, and district</li>
                <li><i className="bi bi-check-circle-fill"></i> A healthier, fitter India by 2030</li>
                <li><i className="bi bi-check-circle-fill"></i> The go-to platform for 1 crore+ fitness seekers</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Core Values ── */}
      <section className="about-values-section">
        <div className="container">
          <div className="about-section-header">
            <span className="about-section-pill">What We Stand For</span>
            <h2 className="about-section-title">Our Core Values</h2>
            <p className="about-section-sub">These principles guide every decision we make — from the gyms we list to the features we build.</p>
          </div>
          <div className="about-values-grid">
            {values.map((v) => (
              <div className="about-value-card" key={v.title} style={{"--val-color": v.color, "--val-bg": v.bg}}>
                <div className="about-value-num">{v.num}</div>
                <div className="about-value-top">
                  <div className="about-value-icon">
                    <i className={`bi ${v.icon}`}></i>
                  </div>
                  <div className="about-value-tag">{v.tagline}</div>
                </div>
                <h3 className="about-value-title">{v.title}</h3>
                <p className="about-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What We Offer ── */}
      <section className="about-offer-section">
        <div className="container">
          <div className="about-section-header">
            <span className="about-section-pill">What We Offer</span>
            <h2 className="about-section-title">Built for Gym Seekers <span>&amp;</span> Gym Owners</h2>
          </div>
          <div className="about-offer-grid">
            <div className="about-offer-panel">
              <div className="about-offer-panel-header">
                <i className="bi bi-person-running"></i>
                <h3>For Gym Seekers</h3>
              </div>
              <ul>
                <li><i className="bi bi-check2"></i> Search 5,000+ verified gyms across 50+ cities</li>
                <li><i className="bi bi-check2"></i> Read real, moderated member reviews</li>
                <li><i className="bi bi-check2"></i> Compare membership plans &amp; prices</li>
                <li><i className="bi bi-check2"></i> Filter by type, amenities, location &amp; budget</li>
                <li><i className="bi bi-check2"></i> Find certified personal trainers near you</li>
                <li><i className="bi bi-check2"></i> Book free trials directly with gyms</li>
                <li><i className="bi bi-check2"></i> Available on web &amp; Android app — 100% free</li>
              </ul>
              <Link to="/varanasi/gym" className="about-offer-cta">
                Find Gyms Near Me <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
            <div className="about-offer-panel about-offer-panel--dark">
              <div className="about-offer-panel-header">
                <i className="bi bi-buildings"></i>
                <h3>For Gym Owners</h3>
              </div>
              <ul>
                <li><i className="bi bi-check2"></i> Free gym listing — reach lakhs of seekers</li>
                <li><i className="bi bi-check2"></i> Professional gym profile with photos &amp; plans</li>
                <li><i className="bi bi-check2"></i> Get direct enquiries from verified leads</li>
                <li><i className="bi bi-check2"></i> Manage member reviews &amp; reputation</li>
                <li><i className="bi bi-check2"></i> Gym management software (billing, attendance)</li>
                <li><i className="bi bi-check2"></i> Gym setup &amp; interior design support</li>
                <li><i className="bi bi-check2"></i> Analytics &amp; growth insights dashboard</li>
              </ul>
              <Link to="/free-gym-listing" className="about-offer-cta about-offer-cta--light">
                List Your Gym Free <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Techup Technologies ── */}
      <section className="about-techup-section">
        <div className="container">
          <div className="about-techup-inner">

            {/* Left — brand story */}
            <div className="about-techup-left">
              <span className="about-techup-badge">
                <i className="bi bi-patch-check-fill me-1"></i> Proudly Made in India
              </span>
              <h2 className="about-techup-title">
                MeraGym is a Product of<br />
                <span className="about-techup-brand">Techup Technologies</span>
              </h2>
              <p className="about-techup-desc">
                Techup Technologies is a Varanasi-based software company building digital products that
                solve real problems for real people. From gym discovery to gym management, we combine
                deep domain expertise with modern technology to create tools that genuinely make a
                difference — for businesses and consumers alike.
              </p>
              <p className="about-techup-desc">
                Beyond MeraGym, Techup builds end-to-end <strong>gym management software</strong>,
                <strong> fitness apps</strong>, and custom technology solutions for the health &amp; fitness industry across India.
              </p>

              <div className="about-techup-products">
                <div className="about-techup-product">
                  <div className="about-techup-product-icon"><i className="bi bi-phone-fill"></i></div>
                  <div>
                    <strong>MeraGym App</strong>
                    <span>India's gym discovery platform</span>
                  </div>
                </div>
                <div className="about-techup-product">
                  <div className="about-techup-product-icon"><i className="bi bi-gear-fill"></i></div>
                  <div>
                    <strong>Gym Management Software</strong>
                    <span>Billing, attendance &amp; member CRM</span>
                  </div>
                </div>
                <div className="about-techup-product">
                  <div className="about-techup-product-icon"><i className="bi bi-palette-fill"></i></div>
                  <div>
                    <strong>Gym Setup &amp; Design</strong>
                    <span>Interior &amp; equipment solutions</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — contact card */}
            <div className="about-techup-right">
              <div className="about-techup-card">
                <div className="about-techup-card-logo">
                  <i className="bi bi-code-slash"></i>
                  <span>Techup Technologies</span>
                </div>
                <p className="about-techup-card-tagline">
                  "We build technology that empowers Indian businesses to grow faster and smarter."
                </p>
                <div className="about-techup-links">
                  <a
                    href="https://techuptechnologies.com"
                    target="_blank"
                    rel="noreferrer"
                    className="about-techup-link-primary"
                  >
                    <i className="bi bi-globe2 me-2"></i>Visit techuptechnologies.com
                  </a>
                  <a
                    href="mailto:techuptechnologies@gmail.com"
                    className="about-techup-link-secondary"
                  >
                    <i className="bi bi-envelope-fill me-2"></i>techuptechnologies@gmail.com
                  </a>
                  <a
                    href="tel:+916390008506"
                    className="about-techup-link-secondary"
                  >
                    <i className="bi bi-telephone-fill me-2"></i>+91 63900 08506
                  </a>
                </div>
                <div className="about-techup-locations">
                  <div className="about-techup-loc">
                    <i className="bi bi-geo-alt-fill"></i>
                    <span>Kamachha, Varanasi, UP</span>
                  </div>
                  <div className="about-techup-loc">
                    <i className="bi bi-geo-alt-fill"></i>
                    <span>Sector 75, Noida, UP</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="about-cta-section">
        <div className="container">
          <div className="about-cta-inner">
            <h2>Ready to Start Your Fitness Journey?</h2>
            <p>Join lakhs of Indians who find their perfect gym on MeraGym — completely free.</p>
            <div className="about-cta-actions">
              <Link to="/varanasi/gym" className="about-btn-primary">
                <i className="bi bi-search me-2"></i>Search Gyms
              </Link>
              <Link to="/contact-us" className="about-btn-outline">
                <i className="bi bi-chat-dots me-2"></i>Talk to Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
